import 'normalize.css/normalize.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.scss';

import { defineComponent, h, onMounted, onUnmounted, provide, shallowRef, watch } from 'vue';
import app, { handle_login_params, initialised, make_application_container } from './application-container';
import parseParams from './util/parseParams';
import Phi from './components/Phi';
import Spinner from './components/Spinner';
import { withSpinner } from './state/spinner';
import { Background } from './Colours';
import { colours } from '@phi/constants';
import { CLARITY, GOOGLE, GTM } from '@phi/config/frontend';
import { absorb_event } from '@phi/util/events';
import on_message from './message';
import { initialise as initialise_analytics } from '@phi/analytics';
import { PopUnderWithRouter } from '@phi/pop-under-with-router';
import { update_viewport_dimensions } from '@phi/components';
import { IFrontendServiceContainer, frontend_service_container, frontend_service_container_provider } from '@phi/frontend/service-container';
import { reference_transparent } from '@features/references';
import { api_provider } from '@phi/frontend/api/provider';
import { add1 } from '@vaaas/fpts/set';
import { SessionType } from '@phi/authentication/frontend';
import { SERVICE_CONTAINER } from '@phi/frontend/injections';
import useCookie, { REQUIRE_COOKIE_KEY } from '@phi/cookies/use-cookie';

function clean_url() {
    window.history.replaceState(null, document.title, '/');
}

function registerServiceWorker() {
    if (!('serviceWorker' in navigator))
        return Promise.resolve(undefined);
    return navigator.serviceWorker.register(
        new URL(location.origin + '/service-worker.js'),
        { scope: '/' }
    ).catch((error) => {
        console.debug(`Service worker registration failed with ${error}`);
        return error;
    });
}

function register_watchers() {
    watch(
        () => app.settings.background,
        (current, old) => {
            if (old) document.body.classList.remove(old);
            document.body.classList.add(current);

            app.state.colours = current === Background.white
                ? colours.dark
                : colours.light;
        },
        { immediate: true }
    );

    watch(
        () => frontend_service_container().authentication.session_type.value,
        (x) => app.state.is_demo = x.has('demo'),
        { immediate: true },
    );

    watch(
        () => app.settings.inset && !app.state.immersive,
        (should_be_inset) => {
            if (should_be_inset)
                document.body.classList.add('inset');
            else
                document.body.classList.remove('inset');
        },
        { immediate: true }
    );

    watch(
        () => app.state.framework,
        (framework) => {
            if (framework === 'onshape') {
                const session_type = frontend_service_container().authentication.session_type;
                session_type.value = add1('onshape' as SessionType)(session_type.value);
            }
        },
        {
            immediate: true,
        },
    );

    watch(
        () => app.settings.reference_transparent,
        (value) => {
            reference_transparent.value = value;
        }
    );
}

let new_user = false;

async function initialise_application() {
    const app = await make_application_container();
    const analytics = await initialise_analytics({
        google_token: GOOGLE,
        gtm_token: GTM,
        clarity_token: CLARITY,
    });
    const service_container = frontend_service_container_provider(
        app.deviceStorage,
        app.state,
        app.settings,
        api_provider,
        analytics,
    );
    const params = parseParams();
    console.log("P3:")
    console.log(params)

    new_user = params.new === 'true';
    handle_login_params(params);
    clean_url();
    register_watchers();
    registerServiceWorker();
}

export default defineComponent({
    setup() {
        provide(SERVICE_CONTAINER, frontend_service_container());
        provide(REQUIRE_COOKIE_KEY, useCookie());
        const container = shallowRef<IFrontendServiceContainer|undefined>(undefined);

        onMounted(async () => {
            await initialise_application();
            container.value = frontend_service_container();

            const {
                authentication,
                device_storage,
                pop_under_with_router,
                subscriptions,
            } = container.value;

            if (authentication.session_type.value.size === 0) {
                if (device_storage.token) {
                    await withSpinner('Signing in...', authentication.validate_session)();
                }
            }
            if (authentication.session_type.value.has('phi')) {
                await authentication.authenticate_through_phi(device_storage.token!);
            } else if (authentication.session_type.value.has('onshape')) {
                await authentication.authenticate_through_onshape();
            }

            if (authentication.session_type.value.size === 0) {
                pop_under_with_router.show_pop_under(false);
            } else if (!app.state.user?.stripe_subscription_id) {
                subscriptions.show_select_a_subscription_page();
            } else {
                app.state.should_load_app = true;
            }

            window.addEventListener('message', on_message);
            document.addEventListener('contextmenu', absorb_event);
            window.addEventListener('resize', update_viewport_dimensions);

            initialised.value = true;
        });

        onUnmounted(() => {
            window.removeEventListener('message', on_message);
            document.removeEventListener('contextmenu', absorb_event);
            window.removeEventListener('resize', update_viewport_dimensions);
        });

        return () => {
            if (!initialised.value)
                return '';
            else return [
                h(Spinner, { text: app.state.spinner }),
                h(PopUnderWithRouter, { app: container.value! }),
                app.state.should_load_app && h(Phi, {
                    app: app,
                    service_container: container.value!,
                }),
            ];
        };
    },
});
